import { Loading } from "@mnk/frontend/components/Loading";
import { PrimaryButton } from "@mnk/frontend/components/ui/button/PrimaryButton";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { Drawing } from "@mnk/shared/models/drawing";
import { useState } from "react";

interface Props {
  projectId: string;
  onClose: () => void;
  drawings: Drawing[];
  reloadDrawings: () => Promise<void>;
}

type DrawingWithNullableSection = Omit<Drawing, "sectionName"> & {
  sectionName: string | null;
};

export const SelectDrawingModal = ({ onClose, drawings, reloadDrawings, projectId }: Props) => {
  const drawingsQuery = trpc.getDrawings.useQuery();
  const { mutate: createProjectDrawing } = trpc.createProjectDrawing.useMutation({
    onSuccess: async () => {
      await reloadDrawings();
      onClose();
    },
  });
  const allDrawings = drawingsQuery.data as DrawingWithNullableSection[] | undefined;
  const [selectedDrawings, setSelectedDrawings] = useState<DrawingWithNullableSection[]>(
    drawings as DrawingWithNullableSection[],
  );

  const [searchTerm, setSearchTerm] = useState("");

  const filteredDrawings = allDrawings?.filter((drawing) =>
    drawing.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const onSave = () => {
    createProjectDrawing(selectedDrawings.map((drawing) => ({ projectId, drawingId: drawing.id })));
  };

  if (drawingsQuery.isLoading) return <Loading />;
  if (drawingsQuery.error) return <div>Error: {drawingsQuery.error.message}</div>;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
    >
      <div className="w-full h-full flex justify-center items-center">
        <div
          className="bg-white rounded-lg shadow-lg w-[640px] max-h-[640px] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onClose();
            }
          }}
        >
          <div className="flex flex-col gap-2">
            <h2 className="text-left text-2xl font-bold p-4 border-b border-gray-300">図面一覧</h2>
            <div className="flex flex-row gap-2 justify-left items-center p-2">
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md p-2"
                placeholder="図面名で検索"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="overflow-y-auto max-h-[300px]">
                <table className="min-w-full border border-gray-300">
                  <thead>
                    <tr>
                      <th className="sticky top-0 bg-gray-200 text-left text-sm font-normal p-2">選択</th>
                      <th className="sticky top-0 bg-gray-200 text-left text-sm font-normal p-2">図面名</th>
                      <th className="sticky top-0 bg-gray-200 text-left text-sm font-normal p-2">KP</th>
                      <th className="sticky top-0 bg-gray-200 text-left text-sm font-normal p-2">区間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrawings?.map((drawing) => (
                      <tr
                        key={drawing.id}
                        className="border-t border-gray-300 cursor-pointer"
                        onClick={() => {
                          setSelectedDrawings((prev) => {
                            if (prev.includes(drawing)) {
                              return prev.filter((d) => d.id !== drawing.id);
                            }
                            return [...prev, drawing];
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setSelectedDrawings((prev) => {
                              if (prev.includes(drawing)) {
                                return prev.filter((d) => d.id !== drawing.id);
                              }
                              return [...prev, drawing];
                            });
                          }
                        }}
                      >
                        <td className="text-center text-sm font-normal bg-white p-2">
                          <input
                            type="checkbox"
                            checked={selectedDrawings.some((d) => d.id === drawing.id)}
                            className="cursor-pointer"
                          />
                        </td>
                        <td className="text-left text-sm font-normal bg-white p-2">{drawing.name}</td>
                        <td className="text-left text-sm font-normal bg-white p-2">
                          {drawing.startKiloPost}-{drawing.endKiloPost}
                        </td>
                        <td className="text-left text-sm font-normal bg-white p-2">{drawing.sectionName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex flex-row gap-2 justify-center items-center p-4 border-t border-gray-300">
            <PrimaryButton onClick={onSave}>追加</PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
